import type {
  CordonNode,
  OperationalMapConfig,
  Origin,
  Segment,
  Train,
  QueueLine,
  QueueStage,
  ExitStage,
  Zone,
  Sector,
  HoldingComplex,
  HoldingRoom,
} from '../types';

export const CONFIG = {
  MAPS_KEY: "AIzaSyDRbkMHXDupqdh7KRy4RBV1VLqfN-xUBR8",
  CAPACITY: 12000,
  DWELL_MIN: 34,
  EXIT_CAP: 640,
  START_TOTAL: 186000,
  START_CLOCK: 10 * 60 + 12
};

export const RAW_NODES: Record<string, Omit<CordonNode, 'id' | 'seed'>> = {
  "STN-MAIN": { name: "Station main gate", lat: 16.51832, lng: 80.61991, kind: "origin", cams: 14 },
  "STN-ET2": { name: "East Terminal 2", lat: 16.51743, lng: 80.61956, kind: "origin", cams: 10 },
  "STN-PARCEL": { name: "Parcel office gate", lat: 16.51477, lng: 80.61779, kind: "origin", cams: 8 },
  "STN-THARA": { name: "Tharapeta entrance", lat: 16.51524, lng: 80.61635, kind: "origin", cams: 11 },
  "PNBS": {
    name: "Pandit Nehru Bus Station",
    lat: 16.508701,
    lng: 80.615857,
    kind: "origin",
    cams: 16,
    q: "Pandit Nehru Bus Station, Vijayawada, Andhra Pradesh"
  },
  "LOCAL": {
    name: "Kummaripalem & ghats",
    lat: 16.51940,   // Lotus Apartments junction toward Kummaripalem Centre (was 16.51120, 80.60350 = in the river)
    lng: 80.60220,
    kind: "origin",
    cams: 12,
    q: "Kummaripalem, Vijayawada, Andhra Pradesh"
  },
  "GADDE": { name: "Gadde Bomma centre", lat: 16.51167, lng: 80.61315, kind: "cordon", cams: 9 },
  "VINAYAKA": { name: "Vinayaka Temple junction", lat: 16.51226, lng: 80.61114, kind: "cordon", cams: 9 },
  "BODDU": { name: "Boddu Bomma centre", lat: 16.51247, lng: 80.60762, kind: "cordon", cams: 10 },
  "VIPGATE": { name: "VIP car entrance", lat: 16.51233, lng: 80.60627, kind: "gate", cams: 5 },
  "QLINE": { name: "Bhavani Ghat Q-line start", lat: 16.51296, lng: 80.60380, kind: "queue", cams: 12 },
  "TEMPLE": { name: "Temple entrance", lat: 16.51322, lng: 80.60376, kind: "temple", cams: 14 },
  "EXIT-KDN": { name: "Main exit — Kanaka Durga Nagar", lat: 16.51488, lng: 80.60688, kind: "exit", cams: 11 },
  "KDNAGAR": {
    name: "Kanaka Durga Nagar dispersal",
    lat: 16.51660,
    lng: 80.60900,
    kind: "exit",
    cams: 6,
    q: "Kanaka Durga Nagar, Vijayawada, Andhra Pradesh"
  }
};

export const NODES: Record<string, CordonNode> = {};
Object.keys(RAW_NODES).forEach(k => {
  NODES[k] = {
    ...RAW_NODES[k],
    id: k,
    seed: { lat: RAW_NODES[k].lat, lng: RAW_NODES[k].lng }
  };
});
export const TEMPLE = NODES["TEMPLE"];

export const OP_MAP: OperationalMapConfig = {
  boundary: [
    { lat: 16.5480, lng: 80.5850 }, { lat: 16.5520, lng: 80.6320 }, { lat: 16.5450, lng: 80.6820 },
    { lat: 16.5230, lng: 80.7040 }, { lat: 16.4890, lng: 80.6960 }, { lat: 16.4750, lng: 80.6500 },
    { lat: 16.4860, lng: 80.6040 }, { lat: 16.5120, lng: 80.5800 }
  ],
  entries: [
    { id: "CITY-N", name: "North · Eluru Road entry", p: { lat: 16.5450, lng: 80.6710 }, to: { lat: 16.5300, lng: 80.6500 }, color: "#FF8A3D" },
    { id: "CITY-E", name: "East · NH-16 / Ramavarappadu", p: { lat: 16.5220, lng: 80.7000 }, to: { lat: 16.5150, lng: 80.6600 }, color: "#F5D547" },
    { id: "CITY-SE", name: "South-east · Machilipatnam Road", p: { lat: 16.4895, lng: 80.6910 }, to: { lat: 16.5020, lng: 80.6550 }, color: "#F472B6" },
    { id: "CITY-S", name: "South · Guntur / NH-16", p: { lat: 16.4785, lng: 80.6380 }, to: { lat: 16.4950, lng: 80.6250 }, color: "#FB7185" },
    { id: "CITY-W", name: "West · Bhavanipuram entry", p: { lat: 16.5140, lng: 80.5825 }, to: { lat: 16.5228, lng: 80.5929 }, color: "#A78BFA" },
    { id: "CITY-NE", name: "North-east · Nunna entry", p: { lat: 16.5480, lng: 80.6820 }, to: { lat: 16.5350, lng: 80.6650 }, color: "#34D399" }
  ],
  holding: [
    { id: "HA-1", name: "Holding Area 1 · VMC & Rajiv Gandhi Park", entry: { lat: 16.510708547632046, lng: 80.61049416925849 }, exit: { lat: 16.511282972815582, lng: 80.61092306037634 } },
    { id: "HA-2", name: "Holding Area 2 · Canal Road & Vinayaka Buffer", entry: { lat: 16.51125058813597, lng: 80.61153353329296 }, exit: { lat: 16.511245518124788, lng: 80.61112610296988 } },
    { id: "HA-3", name: "Holding Area 3 · Bhavanipuram & Babburi Ground", entry: { lat: 16.523391174231183, lng: 80.59289597768635 }, exit: { lat: 16.522752792332767, lng: 80.5940367158544 } },
    { id: "HA-4", name: "Holding Area 4 · Kummaripalem & Ghat Enclosure", entry: { lat: 16.51220, lng: 80.60420 }, exit: { lat: 16.51285, lng: 80.60395 } }
  ],
  merge12: { lat: 16.51132819194173, lng: 80.61100012449573 },
  vinayakaTurn: { lat: 16.512126792873175, lng: 80.61104113886022 },
  tollgate: { lat: 16.51324795235764, lng: 80.60379133339734 },
  tollBridge: { lat: 16.51331481218211, lng: 80.60378198434653 },
  eToilets: { lat: 16.51350922290813, lng: 80.60418557689707 },
  omTurning: { lat: 16.513767975131007, lng: 80.6059198131384 },
  avadhutha: { lat: 16.51512656547344, lng: 80.60583253354962 }
};

const roomState = (index: number, capacity: number) => {
  const occRate = 0.42 + ((index * 19) % 48) / 100;
  const currentCount = Math.max(10, Math.min(capacity, Math.round(capacity * occRate)));
  const gateStatus: HoldingRoom['gateStatus'] = index % 7 === 0 ? 'closed' : index % 2 === 0 ? 'open' : 'metered';
  const ratio = currentCount / capacity;
  const status: HoldingRoom['status'] = ratio >= 0.9 ? 'critical' : ratio >= 0.7 ? 'dense' : 'normal';
  return { currentCount, gateStatus, status };
};

const createHoldingArea1Rooms = (): HoldingRoom[] => {
  // 27 × 40'x20' and 3 × 30'x20' (blocks 5, 10, 15: the short bottom row). Seating = floor(area / 4 sq ft).
  // 2026-10-08 site map: 30 blocks (the earlier drawing had 29). Block 30 is taken as 40'x20' until its size is given.
  return Array.from({ length: 30 }, (_, i) => {
    const blockNum = i + 1;
    const pad = String(blockNum).padStart(2, '0');
    let dim = "40'x20'";
    let areaSqFt = 800;

    if (blockNum === 5 || blockNum === 10 || blockNum === 15) {
      dim = "30'x20'";
      areaSqFt = 600;
    }
    const cap = Math.floor(areaSqFt / 4);
    const state = roomState(blockNum, cap);

    const camA = `CAM-HA1-B${pad}-A`;
    const camB = `CAM-HA1-B${pad}-B`;
    const cams = [camA, camB];

    return {
      id: `HA1-B${pad}`,
      blueprintNumber: String(blockNum),
      name: `Block ${blockNum} (${dim})`,
      te: `బ్లాక్ ${blockNum} (${dim})`,
      dimensions: dim,
      areaSqFt,
      capacity: cap,
      ...state,
      cams,
      coolingFans: true,
      waterAvailable: true,
      medicalPost: blockNum === 5 || blockNum === 15 || blockNum === 25,
      inflowRate: Math.round(20 + (blockNum * 3) % 25),
      outflowRate: Math.round(18 + (blockNum * 2) % 22)
    };
  });
};

const createHoldingArea2Rooms = (): HoldingRoom[] => {
  // Site plan of 15 Oct 2026: 17 boxes numbered 1-17 (the old "16-V" is box 17), as in backend config/holding.json.
  // 1-14 are 30'x25', 15-17 are 40'x25'. Where they sit on the map: HoldingAreaBlueprintModal (layoutRooms).
  return Array.from({ length: 17 }, (_, i) => {
    const idx = i + 1;
    const pad = String(idx).padStart(2, '0');
    const drawingNumber = String(idx);
    const dim = idx <= 14 ? "30'x25'" : "40'x25'";
    const areaSqFt = idx <= 14 ? 750 : 1000;
    const cap = Math.floor(areaSqFt / 4);
    const state = roomState(idx + 4, cap);
    const label = `Block ${drawingNumber} (${dim})`;

    const camA = `CAM-HA2-B${pad}-A`;
    const camB = `CAM-HA2-B${pad}-B`;
    const cams = [camA, camB];

    return {
      id: `HA2-B${pad}`,
      blueprintNumber: drawingNumber,
      name: label,
      te: `బ్లాక్ ${idx} (${dim})`,
      dimensions: dim,
      areaSqFt,
      capacity: cap,
      ...state,
      cams,
      coolingFans: true,
      waterAvailable: true,
      medicalPost: idx === 1 || idx === 16 || idx === 20,
      inflowRate: Math.round(18 + (idx * 4) % 24),
      outflowRate: Math.round(16 + (idx * 3) % 20)
    };
  });
};

const createHoldingArea3Rooms = (): HoldingRoom[] => {
  // Drawing: 33 boxes; blocks 9 and 18 are 35'x30', the other 31 are 35'x20'.
  return Array.from({ length: 33 }, (_, i) => {
    const blockNum = i + 1;
    const pad = String(blockNum).padStart(2, '0');
    const isDeep = blockNum === 9 || blockNum === 18;
    const dim = isDeep ? "35'x30'" : "35'x20'";
    const areaSqFt = isDeep ? 1050 : 700;
    const capacity = Math.floor(areaSqFt / 4);
    const state = roomState(blockNum + 8, capacity);
    return {
      id: `HA3-B${pad}`,
      blueprintNumber: String(blockNum),
      name: `Block ${blockNum} (${dim})`,
      te: `బ్లాక్ ${blockNum} (${dim})`,
      dimensions: dim,
      areaSqFt,
      capacity,
      ...state,
      cams: [`CAM-HA3-B${pad}-A`, `CAM-HA3-B${pad}-B`],
      coolingFans: true,
      waterAvailable: true,
      medicalPost: blockNum === 9 || blockNum === 18 || blockNum === 27,
      inflowRate: 17 + (blockNum * 4) % 23,
      outflowRate: 16 + (blockNum * 3) % 21
    };
  });
};

export const HOLDING_COMPLEXES: HoldingComplex[] = [
  {
    id: "HA-1",
    name: "Holding Area 1 · Pilgrim Holding Complex",
    te: "హోల్డింగ్ ఏరియా 1 · భక్తుల హోల్డింగ్ ప్రాంగణం",
    sector: "S-21 / S-22",
    location: "Corpus Bridge Road · Shed 1 (160'x160') & Shed 2 (60'x40')",
    totalCapacity: 5850,
    declaredCapacity: 6000,
    gatesOpen: true,
    releaseCorridor: "Dedicated release corridor feeds into S-23 → Vinayaka Temple Q-Lines",
    cctvCount: 58,
    status: "active",
    rooms: createHoldingArea1Rooms()
  },
  {
    id: "HA-2",
    name: "Holding Area 2 · Rajiv Gandhi Park & Canal Road Holding Area",
    te: "హోల్డింగ్ ఏరియా 2 · రాజీవ్ గాంధీ పార్క్ & కెనాల్ రోడ్ హోల్డింగ్ ఏరియా",
    sector: "S-22 / S-23",
    location: "Canal Road Junction · Adjacent to Water Canal & VMC Offices",
    totalCapacity: 3368,      // 17 boxes: 14 x 187 + 3 x 250
    declaredCapacity: 4500,
    gatesOpen: true,
    releaseCorridor: "Direct feeder to Sector S-1 Radham Centre & Boddu Bomma",
    cctvCount: 17,            // one camera per box
    status: "active",
    rooms: createHoldingArea2Rooms()
  },
  {
    id: "HA-3",
    name: "Holding Area 3 · Bhavanipuram Buffer Complex",
    te: "హోల్డింగ్ ఏరియా 3 · భవానీపురం బఫర్ ప్రాంగణం",
    sector: "S-20 / Bhavanipuram",
    location: "Bhavanipuram · Babburi Ground approach",
    totalCapacity: 5949,
    declaredCapacity: 6000,
    gatesOpen: true,
    releaseCorridor: "Bhavanipuram feeder → Kummaripalem merge → S-4 Q-lines",
    cctvCount: 66,
    status: "active",
    rooms: createHoldingArea3Rooms()
  }
];


export const ORIGINS: Origin[] = [
  { id: "STN-MAIN", share: 0.16, mode: "Rail" },
  { id: "STN-THARA", share: 0.13, mode: "Rail" },
  { id: "STN-ET2", share: 0.10, mode: "Rail" },
  { id: "STN-PARCEL", share: 0.07, mode: "Rail" },
  { id: "PNBS", share: 0.29, mode: "APSRTC" },
  { id: "LOCAL", share: 0.25, mode: "Walk-in" }
];

export const INITIAL_SEGMENTS: Segment[] = [
  { id: "F1", from: "STN-MAIN", to: "GADDE", kind: "feeder", travel: "WALKING", width: 7.0 },
  { id: "F2", from: "STN-THARA", to: "GADDE", kind: "feeder", travel: "WALKING", width: 6.0 },
  { id: "F3", from: "STN-ET2", to: "GADDE", kind: "feeder", travel: "WALKING", width: 6.0 },
  { id: "F4", from: "STN-PARCEL", to: "GADDE", kind: "feeder", travel: "WALKING", width: 5.0 },
  { id: "T1", from: "GADDE", to: "VINAYAKA", kind: "trunk", travel: "WALKING", width: 11.0 },
  { id: "F5", from: "PNBS", to: "VINAYAKA", kind: "feeder", travel: "WALKING", width: 9.0 },
  { id: "T2", from: "VINAYAKA", to: "BODDU", kind: "trunk", travel: "WALKING", width: 10.0 },
  { id: "F6", from: "LOCAL", to: "BODDU", kind: "feeder", travel: "WALKING", width: 7.0 },
  { id: "V1", from: "BODDU", to: "VIPGATE", kind: "vip", travel: "DRIVING", width: 7.0, share: 0.03 },
  { id: "T3", from: "BODDU", to: "QLINE", kind: "trunk", travel: "WALKING", width: 8.0, share: 0.97 },
  { id: "V2", from: "VIPGATE", to: "TEMPLE", kind: "vip", travel: "DRIVING", width: 6.0 },
  { id: "Q1", from: "QLINE", to: "TEMPLE", kind: "queue", travel: "WALKING", width: 10.0 },
  { id: "X1", from: "TEMPLE", to: "EXIT-KDN", kind: "exit", travel: "WALKING", width: 7.0 },
  { id: "X2", from: "EXIT-KDN", to: "KDNAGAR", kind: "exit", travel: "WALKING", width: 9.0 }
];

export const ORDER = [
  "STN-MAIN", "STN-THARA", "STN-ET2", "STN-PARCEL", "PNBS", "LOCAL",
  "GADDE", "VINAYAKA", "BODDU", "VIPGATE", "QLINE", "TEMPLE", "EXIT-KDN", "KDNAGAR"
];

export const isOut = (s: Segment) => s.kind === "exit";

export const TRAINS: Train[] = [
  { no: "12711", name: "Pinakini Express", from: "Chennai" },
  { no: "17225", name: "Amaravati Express", from: "Hubballi" },
  { no: "12739", name: "Garib Rath", from: "Secunderabad" },
  { no: "20707", name: "Vande Bharat", from: "Visakhapatnam" },
  { no: "12706", name: "Guntur Intercity", from: "Guntur" }
];

export const LAG: Record<string, [number, number]> = {
  "STN-MAIN": [24, 31],
  "STN-THARA": [19, 25],
  "STN-ET2": [23, 30],
  "STN-PARCEL": [21, 27],
  "PNBS": [16, 22],
  "LOCAL": [5, 9]
};

export const FREE_SPEED: Record<string, number> = {
  feeder: 60,
  trunk: 45,
  queue: 24,
  exit: 55,
  vip: 220
};

export const D_CRIT = 1.8;
export const D_JAM = 5.4;

export const QLINES: QueueLine[] = [
  { id: "G1", name: "Q1 · General Line 1", te: "క్యూ 1 · సాధారణ లైన్ 1", share: 0.28, rate: 48, width: 1.5, col: "#2148D6" },
  { id: "G2", name: "Q2 · General Line 2", te: "క్యూ 2 · సాధారణ లైన్ 2", share: 0.24, rate: 46, width: 1.5, col: "#1E8A4C" },
  { id: "G3", name: "Q3 · General Line 3", te: "క్యూ 3 · సాధారణ లైన్ 3", share: 0.20, rate: 44, width: 1.5, col: "#D63A22" },
  { id: "G4", name: "Q4 · Special / ₹100 Line 4", te: "క్యూ 4 · ప్రత్యేక లైన్ 4", share: 0.15, rate: 42, width: 1.5, col: "#7A1FA2" },
  { id: "G5", name: "Q5 · VIP / ₹300-₹500 Line 5", te: "క్యూ 5 · వి.ఐ.పి లైన్ 5", share: 0.13, rate: 40, width: 1.5, col: "#E9C019" }
];

export const QSTAGES: QueueStage[] = [
  { id: "QS1", sector: "S-4", name: "Vinayaka Temple to Toll Gate", te: "వినాయక గుడి నుండి టోల్‌గేట్", len: 260, cams: 2 },
  { id: "QS2", sector: "S-5", name: "Kummaripalem to Head Water Works", te: "కుమ్మరిపాలెం నుండి హెడ్ వాటర్ వర్క్స్", len: 140, cams: 2 },
  { id: "QS3", sector: "S-6", name: "Head Water Works to Toll Gate", te: "హెడ్ వాటర్ వర్క్స్ నుండి టోల్‌గేట్", len: 180, cams: 3 },
  { id: "QS4", sector: "S-7", name: "Toll Gate to OM Turning", te: "టోల్‌గేట్ నుండి ఓం టర్నింగ్", len: 380, cams: 3 },
  { id: "QS5", sector: "S-8", name: "OM Turning to Old Rajagopuram Approach", te: "ఓం టర్నింగ్ నుండి పాత రాజగోపురం", len: 240, cams: 3 },
  { id: "QS6", sector: "S-9", name: "Old Rajagopuram Approach to Antharalayam Exit", te: "రాజగోపురం నుండి అంతరాలయం ఎగ్జిట్", len: 200, cams: 2, darshan: true }
];

export const STAGE_ACTIVE_LINES: Record<string, string[]> = {
  "QS1": ["G1", "G2", "G3"],             // S-4: Q1, Q2, Q3, -, -
  "QS2": ["G1", "G2"],                   // S-5: Q1, Q2, -, -, -
  "QS3": ["G1"],                         // S-6: Q1, -, -, -, -
  "QS4": ["G1", "G2", "G3"],             // S-7: Q1, Q2, Q3, -, -
  "QS5": ["G1", "G2", "G3", "G4", "G5"], // S-8: all five Qs
  "QS6": ["G1", "G2", "G3", "G4", "G5"], // S-9: all five Qs
};

export const XSTAGES: ExitStage[] = [
  { id: "XS1", sector: "S-10", name: "Ravi Chettu open place", te: "రావి చెట్టు", len: 90, width: 14, cams: 2 },
  { id: "XS2", sector: "S-12", name: "Saree counter · Sivalayam", te: "చీరల కౌంటర్", len: 140, width: 9, cams: 2 },
  { id: "XS3", sector: "S-13", name: "Mahamandapam lifts & stairs", te: "లిఫ్టులు & మెట్లు", len: 110, width: 8, cams: 3 },
  { id: "XS4", sector: "S-14", name: "Iron steps descent", te: "ఐరన్ మెట్లు", len: 180, width: 6, cams: 3 },
  { id: "XS5", sector: "S-16", name: "Annadanam", te: "అన్నదానం", len: 80, width: 12, cams: 2 },
  { id: "XS6", sector: "S-17", name: "Prasadam & laddu counters", te: "ప్రసాదం కౌంటర్లు", len: 120, width: 7, cams: 2 },
  { id: "XS7", sector: "S-15", name: "Kanaka Durga Nagar exit", te: "కనకదుర్గ నగర్ ఎగ్జిట్", len: 150, width: 10, cams: 2 }
];

export const ZONES: Zone[] = [
  { z: "A", name: "River Road — Entry", te: "నదీ రహదారి ప్రవేశం" },
  { z: "B", name: "Hill Climb", te: "కొండ ఎక్కే మార్గం" },
  { z: "C", name: "Temple Core", te: "ఆలయ ప్రాంగణం" },
  { z: "D", name: "Darshan Exit & Prasadam", te: "నిష్క్రమణ & ప్రసాదం" },
  { z: "E", name: "River Road — Return", te: "నదీ రహదారి తిరుగు మార్గం" },
  { z: "F", name: "Ghats, Holding & Transit", te: "ఘాట్లు · హోల్డింగ్ · రవాణా" }
];

export const SECTORS: Sector[] = [
  { c: "S-1", z: "A", n: "Vinayaka Temple → Radham Centre", ex: 7, cam: 14, f: ["Cloak room", "5 street exits east"] },
  { c: "S-2", z: "A", n: "Radham Centre → Model Guest House FOB", ex: 2, cam: 10, f: ["Tourist outpost", "Arjun Street"] },
  { c: "S-3", z: "A", n: "Foot-over-bridge → Tollgate", ex: 2, cam: 11, f: ["Durga Ghat underpass", "Water tank", "Gayatri Matam"] },
  { c: "S-4", z: "A", n: "Kummaripalem Jn → Tollgate", ex: 3, cam: 12, f: ["Q-LINES START", "Head Water Works"], q: "QS1" },
  { c: "S-5", z: "A", n: "Tollgate", ex: 2, cam: 9, f: ["Ticket gates", "Anjaneya Swamy temple"], q: "QS2" },
  { c: "S-6", z: "B", n: "Tollgate → OM Turning", ex: 4, cam: 13, f: ["E-toilets", "Cloak · Cheppal · Pongal sheds"], q: "QS3" },
  { c: "S-7", z: "B", n: "OM Turning → Rajagopuram", ex: 3, cam: 12, f: ["VIP way", "2 foot-over-bridges", "Media point"], q: "QS4" },
  { c: "S-8", z: "C", n: "Raja Gopuram Steps & Prakara Mandapam", ex: 2, cam: 15, f: ["VVIP · VIP · Old steps", "5 free + service line", "4 gates"], q: "QS5" },
  { c: "S-9", z: "C", n: "Antharalayam — darshan", ex: 2, cam: 10, f: ["All lines merge", "Front-gate exit walkway"], q: "QS6" },
  { c: "S-10", z: "C", n: "Ravi Chettu Open Place", ex: 2, cam: 11, f: ["Old + new lifts", "2 staircases", "Police OP"], x: "XS1" },
  { c: "S-11", z: "C", n: "Nataraja Swamy → Avadhutha Mouna Swamy", ex: 2, cam: 8, f: ["₹500/₹300 line", "Free/₹100 line", "VIP exit ramp"] },
  { c: "S-12", z: "D", n: "Saree Counter → Rayabhara Mandapam & Sivalayam", ex: 2, cam: 9, f: ["Sivalayam darshan loop", "Chandi Yagam"], x: "XS2" },
  { c: "S-13", z: "D", n: "New Raja Gopuram · Mahamandapam stairs & lifts", ex: 3, cam: 12, f: ["VIP exit + 2 general exits", "Ramp exit"], x: "XS3" },
  { c: "S-14", z: "D", n: "Rayabhara Mandapam → Iron Steps", ex: 2, cam: 10, f: ["Step-by-step descent", "Yagasala"], x: "XS4" },
  { c: "S-15", z: "D", n: "Mahamandapam → Kanaka Durga Nagar Exit", ex: 3, cam: 13, f: ["Prasadham counters", "WC ♀ ♂", "Gosala · Caves"], x: "XS7" },
  { c: "S-16", z: "D", n: "Annadanam", ex: 1, cam: 6, f: ["On the exit route"], x: "XS5" },
  { c: "S-17", z: "D", n: "Laddu Counter", ex: 1, cam: 6, f: ["One-way flow only", "Laddu making unit"], x: "XS6" },
  { c: "S-18", z: "E", n: "China Wall → Uttaradhi Matam", ex: 2, cam: 9, f: ["East lane southbound", "KD Nagar joins here"] },
  { c: "S-19", z: "E", n: "Uttaradhi Matam → Vinayaka Temple", ex: 7, cam: 11, f: ["Street-wise dispersal into old city"] },
  { c: "S-20", z: "F", n: "Vinayaka Temple → VMC Office (Corpus Bridge)", ex: 2, cam: 7, f: ["East side of bridge road"] },
  { c: "S-21", z: "F", n: "VMC Office → Rajiv Gandhi Park · Holding entry", ex: 2, cam: 8, f: ["Seneeswara Alayam", "Holding-area entry"] },
  { c: "S-22", z: "F", n: "HOLDING AREA", ex: 1, cam: 10, f: ["Single entry · single exit", "Full CCTV coverage"], hold: true },
  { c: "S-23", z: "F", n: "Holding Area → Vinayaka Temple", ex: 1, cam: 6, f: ["Dedicated release corridor", "Feeds S-1 Q-lines"] },
  { c: "S-24", z: "F", n: "Seethammavari Padalu Ghat", ex: 3, cam: 9, f: ["Padmavathi & Krishnaveni ghats", "Changing tents"] },
  { c: "S-25", z: "F", n: "Kesakandanasala", ex: 2, cam: 7, f: ["Hair-tonsuring hall"] },
  { c: "S-26", z: "F", n: "Ghat → Tollgate full approach", ex: 2, cam: 8, f: ["Links S-24 → S-25 → S-22 → S-3 → S-5"] },
  { c: "S-27", z: "F", n: "Durga Ghat · Pindalarevu Ghat", ex: 2, cam: 9, f: ["Laddu making unit", "Prasadam counters"] },
  { c: "S-28", z: "F", n: "Punnami Ghat · Bhavani Ghat", ex: 3, cam: 10, f: ["Overflow bathing & parking", "Boat jetty"] },
  { c: "S-29", z: "F", n: "PNBS — Pandit Nehru Bus Station", ex: 4, cam: 16, f: ["~800 m walk to holding area"] },
  { c: "S-30", z: "F", n: "Vijayawada Railway Station", ex: 5, cam: 18, f: ["~1.5 km walk", "Joins S-1 Q-lines"] }
];

export const SHORT_NAMES: Record<string, string> = {
  "STN-MAIN": "Station main gate", "STN-THARA": "Tharapeta gate", "STN-ET2": "East Terminal 2",
  "STN-PARCEL": "Parcel office gate", "PNBS": "Bus station", "LOCAL": "Kummaripalem & ghats",
  "GADDE": "Gadde Bomma", "VINAYAKA": "Vinayaka junction", "BODDU": "Boddu Bomma",
  "VIPGATE": "VIP entrance", "QLINE": "Queue line starts", "TEMPLE": "TEMPLE",
  "EXIT-KDN": "Main exit", "KDNAGAR": "Kanaka Durga Nagar"
};

export const FLOW_POS: Record<string, [number, number]> = {
  "STN-MAIN": [130, 92],
  "STN-THARA": [130, 168],
  "STN-ET2": [130, 245],
  "STN-PARCEL": [130, 321],
  "PNBS": [130, 398],
  "LOCAL": [130, 474],

  "GADDE": [370, 207],
  "VINAYAKA": [600, 290],
  "BODDU": [830, 340],
  "QLINE": [1060, 260],
  "VIPGATE": [1060, 420],
  "TEMPLE": [1290, 340],
  "EXIT-KDN": [1510, 340],
  "KDNAGAR": [1720, 340]
};

export const FLOW_COLHDR: [number, string][] = [
  [130, "1. Entry Gates"],
  [370, "2. Feeder Merge"],
  [600, "3. Central Junction"],
  [830, "4. Hill Approach"],
  [1060, "5. Queue & VIP"],
  [1290, "6. Sanctum"],
  [1510, "7. Clearance Exit"],
  [1720, "8. Dispersal"]
];

export const BP = { x0: 150, y0: 392, lane: 25, laneH: 17, exitY: 726, exitH: 132, W: 1920, H: 1210 };

export const BP_FEATURES = [
  { s: "QS1", o: 0.08, side: -1, t: "KUMMARIPALEM JUNCTION", sub: "Q-lines start · S-4" },
  { s: "QS1", o: 0.52, side: 1, t: "HEAD WATER WORKS", g: "P" },
  { s: "QS1", o: 0.88, side: -1, t: "→ FOUR PILLAR CENTER" },
  { s: "QS2", o: 0.50, side: 1, t: "TICKET GATES", sub: "S-5 Tollgate", g: "CLK" },
  { s: "QS3", o: 0.10, side: -1, t: "SRI KRISHNA STATUE" },
  { s: "QS3", o: 0.26, side: 1, t: "E-TOILETS", g: "WC" },
  { s: "QS3", o: 0.44, side: -1, t: "OM TURNING", sub: "hairpin · S-6" },
  { s: "QS3", o: 0.62, side: 1, t: "CLOAK · CHEPPAL · PONGAL SHEDS", g: "CLK" },
  { s: "QS3", o: 0.80, side: -1, t: "ROUND SHED · 4 EMERGENCY EXITS", g: "EXIT" },
  { s: "QS4", o: 0.14, side: 1, t: "Y-JUNCTION" },
  { s: "QS4", o: 0.34, side: -1, t: "FOOT-OVER-BRIDGE ×2" },
  { s: "QS4", o: 0.54, side: 1, t: "VIP WAY", sub: "green corridor" },
  { s: "QS4", o: 0.74, side: -1, t: "MEDIA POINT" },
  { s: "QS4", o: 0.92, side: 1, t: "OLD RAJA GOPURAM", sub: "S-7", g: "T" },
  { s: "QS5", o: 0.35, side: 1, t: "DIVIDING POINT", sub: "S-8 · 5 free + service" }
];

export const BP_EXITF = [
  { s: "XS1", t: "RAVI CHETTU OPEN PLACE", g: "P" },
  { s: "XS2", t: "SAREE COUNTER · SIVALAYAM LOOP" },
  { s: "XS3", t: "MAHAMANDAPAM LIFTS & STAIRS", g: "EXIT" },
  { s: "XS4", t: "IRON STEPS" },
  { s: "XS5", t: "ANNADANAM" },
  { s: "XS6", t: "PRASADAM · LADDU COUNTERS" },
  { s: "XS7", t: "KANAKA DURGA NAGAR EXIT", g: "WC" }
];

export const GLYPH: Record<string, string> = { P: "P", CLK: "CLK", WC: "WC", EXIT: "EXIT", T: "▲" };

export const BP_APPROACH = [
  { c: "S-30", n: "Railway station", lines: ["Railway station"] },
  { c: "S-29", n: "PNBS bus station", lines: ["PNBS bus", "station"] },
  { c: "S-28", n: "Punnami · Bhavani ghats", lines: ["Punnami ·", "Bhavani ghats"] },
  { c: "S-24", n: "Seethammavari Padalu", lines: ["Seethammavari", "Padalu"] },
  { c: "S-25", n: "Kesakandanasala", lines: ["Kesakandanasala"] },
  { c: "S-20", n: "Corpus Bridge → VMC Office", lines: ["Corpus Bridge →", "VMC Office"] },
  { c: "S-21", n: "Rajiv Gandhi Park · holding entry", lines: ["Rajiv Gandhi Park", "holding entry"] },
  { c: "S-22", n: "HOLDING AREA", hold: true, lines: ["HOLDING AREA"] },
  { c: "S-23", n: "Release corridor", lines: ["Release corridor"] },
  { c: "S-1", n: "Vinayaka Temple", lines: ["Vinayaka Temple"] },
  { c: "S-2", n: "Radham Centre", lines: ["Radham Centre"] },
  { c: "S-3", n: "FOB · Durga Ghat underpass", lines: ["FOB · Durga Ghat", "underpass"] },
  { c: "S-26/27", n: "Ghat → Tollgate approach", lines: ["Ghat → Tollgate", "approach"] }
];

export const BP_ZONES = [
  { z: "A", n: "River Road — Entry", te: "నదీ రహదారి ప్రవేశం", s: "S-1 … S-5" },
  { z: "B", n: "Hill Climb", te: "కొండ ఎక్కే మార్గం", s: "S-6 · S-7" },
  { z: "C", n: "Temple Core", te: "ఆలయ ప్రాంగణం", s: "S-8 … S-11" },
  { z: "D", n: "Exit & Prasadam", te: "నిష్క్రమణ & ప్రసాదం", s: "S-12 … S-17" },
  { z: "E", n: "River Road — Return", te: "తిరుగు మార్గం", s: "S-18 · S-19 · S-27" },
  { z: "F", n: "Ghats · Holding · Transit", te: "ఘాట్లు · హోల్డింగ్", s: "S-20 … S-30" }
];

export const BP_RETURN = [
  { c: "S-18", n: "China Wall → Uttaradhi Matam" },
  { c: "S-19", n: "Uttaradhi Matam → Vinayaka Temple · 7 exits" },
  { c: "S-1", n: "Street-wise dispersal into old city" },
  { c: "S-29/30", n: "Back to PNBS & Railway station" }
];

export const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0C1620" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0A0F16" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6C7F94" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "on" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#16222E" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#1B2A38" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#233549" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#5B6D83" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#071B26" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#0E1A22" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#243444" }] }
];

export const MAP_STYLE_LIGHT = [
  { elementType: "geometry", stylers: [{ color: "#F4F6F9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "on" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#E2E8F0" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#CBD5E1" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#94A3B8" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#334155" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#F1F5F9" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#CBD5E1" }] }
];

export const SAT_STYLE = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#F2F6FA" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0A0F16" }, { weight: 3 }] }
];
